import { useGetQueryParams } from '@cp/shared/queryParams';
import { PropsWithChildren, createContext, useContext } from 'react';
import { InferType, number, object, string } from 'yup';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppFormProvider } from '@cp/utils/form';
import { Stack } from '@cp/ds/src/components/stack';
import { DEFAULT_FILTERS_VALUES } from '../../lib/config';
import { GetItems, useGetItems } from '../../model/useGetItems';

interface IContext {
  data: GetItems;
  form: ReturnType<typeof useForm<ProcessingAndReviewsFiltersType>>;
}

const Context = createContext<() => IContext>(() => {
  throw new Error('ProcessingAndReviews context not provided');
});

export const filtersSchema = object({
  page: number().required(),
  cityId: number().optional(),
  phoneOrEmail: string().optional().min(3),
  clientType: number().required(),
  careerId: number().required(),
  dealStatus: number().optional(),
  typeOfItems: number().required(),
  moderationStatus: number().optional(),
  rejectionReason: number().optional(),
});

export type ProcessingAndReviewsFiltersType = InferType<typeof filtersSchema>;
export type ProcessingAndReviewsQueryParams = Partial<ProcessingAndReviewsFiltersType>;

export const useProcessingAndReviews = () => {
  const c = useContext(Context);
  return c();
};

export const ProcessingAndReviews = ({ children }: PropsWithChildren) => {
  const queryParams = useGetQueryParams<ProcessingAndReviewsQueryParams>();
  const form = useForm<ProcessingAndReviewsFiltersType>({
    defaultValues: {
      ...DEFAULT_FILTERS_VALUES,
      ...queryParams,
    },
    resolver: yupResolver(filtersSchema) as Resolver<ProcessingAndReviewsFiltersType>,
  });
  const data = useGetItems();

  const value: () => IContext = () => ({
    form,
    data,
  });

  return (
    <AppFormProvider form={form}>
      <Context.Provider value={value}>
        <Stack spacing={2.5}>{children}</Stack>
      </Context.Provider>
    </AppFormProvider>
  );
};
